<template>
    <section class="px-3 lg:px-28 py-16 lg:py-24">
        <div class="text-center mb-8">
            <h2 class="font-semibold">What would it cost to build these integrations yourself?</h2>
            <p class="subtitle mt-4 max-w-3xl mx-auto">
                Estimate the build time, the ongoing maintenance team and the first-year cost of building in-house,
                and compare it with Unified. Every assumption is editable under Advanced.
            </p>
        </div>

        <div class="max-w-5xl mx-auto">
            <div
                class="border border-gray-700/20 rounded-3xl shadow-lg shadow-gray-700/20 bg-gradient-to-br from-gray-900 via-gray-900/95 to-blue-950/10 p-6 sm:p-8">
                <div class="grid md:grid-cols-2 gap-8">
                    <form class="text-sm lg:text-base space-y-6 text-gray-400" @submit.prevent>
                        <fieldset>
                            <legend class="text-white font-semibold mb-2">Step 1 — Integration scope</legend>
                            <p class="mb-1">I am looking to build integrations for:</p>
                            <p class="text-gray-500 mb-3 text-xs">Some categories are more complex to build and
                                maintain. Your selection helps estimate effort more accurately.</p>
                            <div class="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
                                <label v-for="cat in categoryOptions" :key="cat.id" class="flex items-center gap-2">
                                    <input v-model="formData.categories" type="checkbox" :value="cat.id" />
                                    {{ cat.label }}
                                </label>
                            </div>
                        </fieldset>

                        <label class="flex flex-wrap items-baseline gap-x-2">
                            <span>I need</span>
                            <input v-model.number="formData.numberOfNewIntegrations" type="number" min="1" step="1"
                                :class="INPUT" class="w-16" />
                            <span>new integrations for my product.</span>
                            <span class="w-full text-gray-500 mt-1 text-xs">This drives both the total build cost and
                                the overall timeline.</span>
                        </label>

                        <fieldset class="space-y-6">
                            <legend class="text-white font-semibold mb-2">Step 2 — Company context</legend>
                            <ToolHint hint="The more integrations you have, the easier it is to build a new one.">
                                <label class="flex flex-wrap items-baseline gap-x-2">
                                    <span>I already built</span>
                                    <input v-model.number="formData.numberOfExistingIntegrations" type="number"
                                        min="0" :class="INPUT" class="w-16" />
                                    <span>integrations in my product.</span>
                                </label>
                            </ToolHint>
                            <ToolHint hint="The longer you're in business, the more legacy and tech debt you have to deal with.">
                                <label class="flex flex-wrap items-baseline gap-x-2">
                                    <span>I have been in business for</span>
                                    <input v-model.number="formData.yearsInBusiness" type="number" min="0"
                                        :class="INPUT" class="w-16" />
                                    <span>years.</span>
                                </label>
                            </ToolHint>
                        </fieldset>

                        <fieldset class="space-y-6">
                            <legend class="text-white font-semibold mb-2">Step 3 — Resources & maintenance</legend>
                            <label class="flex flex-wrap items-baseline gap-x-2">
                                <span>I will be making</span>
                                <input v-model.number="formData.numberOfAPICallsPerMonth" type="number" min="0"
                                    :class="INPUT" class="w-32" />
                                <span>API calls per month.</span>
                            </label>
                            <ToolHint hint="You will need developers to build and to maintain the integrations.">
                                <label class="flex flex-wrap items-baseline gap-x-2">
                                    <span>My developers earn $</span>
                                    <input v-model.number="formData.developersAnnualSalary" type="number" min="0"
                                        :class="INPUT" class="w-28" />
                                    <span>per year.</span>
                                </label>
                            </ToolHint>
                            <ToolHint hint="1 product manager for 6 developers to build integrations; PMs for maintenance scale with the number of integrations.">
                                <label class="flex flex-wrap items-baseline gap-x-2">
                                    <span>My product managers earn $</span>
                                    <input v-model.number="formData.productManagersAnnualSalary" type="number" min="0"
                                        :class="INPUT" class="w-28" />
                                    <span>per year.</span>
                                </label>
                            </ToolHint>
                            <ToolHint hint="More API requests mean more infrastructure to run, so more dev-ops time.">
                                <label class="flex flex-wrap items-baseline gap-x-2">
                                    <span>My dev-ops people earn $</span>
                                    <input v-model.number="formData.devOpsAnnualSalary" type="number" min="0"
                                        :class="INPUT" class="w-28" />
                                    <span>per year.</span>
                                </label>
                            </ToolHint>
                            <label class="flex items-center gap-2 text-sm">
                                <input v-model="formData.showRange" type="checkbox" />
                                Show a ±15% range on complexity
                            </label>
                        </fieldset>

                        <details>
                            <summary class="cursor-pointer select-none text-gray-300">Advanced</summary>
                            <div class="mt-4 space-y-6">
                                <ToolHint hint="Baseline weeks to build a typical integration, before complexity, legacy and learning factors.">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>Base time to build one integration is</span>
                                        <input v-model.number="formData.baseBuildWeeks" type="number" min="1"
                                            :class="INPUT" class="w-16" />
                                        <span>weeks.</span>
                                    </label>
                                </ToolHint>
                                <ToolHint hint="Support workload is tickets per integration times hours per ticket.">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>My technical support people earn $</span>
                                        <input v-model.number="formData.supportAnnualSalary" type="number" min="0"
                                            :class="INPUT" class="w-28" />
                                        <span>per year.</span>
                                    </label>
                                </ToolHint>
                                <div class="flex flex-wrap items-baseline gap-x-2">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>Support tickets per integration per year</span>
                                        <input v-model.number="formData.ticketsPerIntegrationPerYear" type="number"
                                            min="0" :class="INPUT" class="w-20" />
                                    </label>
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>×</span>
                                        <input v-model.number="formData.hoursPerTicket" type="number" step="0.1"
                                            min="0" :class="INPUT" class="w-20" aria-label="Hours per ticket" />
                                        <span>hours per ticket.</span>
                                    </label>
                                </div>
                                <ToolHint hint="One-off or upfront third-party or partner costs while building.">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>Partner fees (build) $</span>
                                        <input v-model.number="formData.partnerFeesBuild" type="number" min="0"
                                            :class="INPUT" class="w-28" />
                                    </label>
                                </ToolHint>
                                <ToolHint hint="Recurring third-party or partner costs for maintenance.">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>Partner fees (maintenance) $</span>
                                        <input v-model.number="formData.partnerFeesMaintenance" type="number" min="0"
                                            :class="INPUT" class="w-28" />
                                        <span>per year.</span>
                                    </label>
                                </ToolHint>
                                <ToolHint hint="Your expected Unified subscription, for the comparison. See /pricing for plans.">
                                    <label class="flex flex-wrap items-baseline gap-x-2">
                                        <span>Unified monthly cost $</span>
                                        <input v-model.number="formData.unifiedMonthlyCost" type="number" min="0"
                                            :class="INPUT" class="w-28" />
                                        <span>per month.</span>
                                    </label>
                                </ToolHint>
                            </div>
                        </details>
                    </form>

                    <aside class="space-y-6 md:sticky md:top-6 self-start" aria-live="polite">
                        <div class="border border-gray-700/50 rounded-2xl p-4">
                            <h3 class="text-lg font-bold text-white mb-2">Build cost</h3>
                            <p class="text-blue-400">Total build: <b>{{ money(build_cost.costForAllIntegrations) }}</b></p>
                            <p class="text-blue-400">Timeline: <b>{{ build_cost.monthsForAllIntegrations }}</b> months</p>
                            <p class="text-gray-400 mt-1">Per integration: {{ build_cost.monthsForEachIntegration }}
                                months, {{ money(build_cost.costForEachIntegration) }}</p>
                            <p v-if="formData.showRange" class="text-gray-500 mt-1">Range: {{
                                (build_math.p10Weeks / 4).toFixed(2) }}–{{ (build_math.p90Weeks / 4).toFixed(2) }}
                                months each</p>
                        </div>

                        <div class="border border-gray-700/50 rounded-2xl p-4">
                            <h3 class="text-lg font-bold text-white mb-2">Maintenance cost</h3>
                            <p class="text-blue-400">Annual: <b>{{ money(maintenance_cost.annualCost) }}</b></p>
                            <p class="text-blue-400">Monthly: <b>{{ money(maintenance_cost.monthlyCost) }}</b></p>
                            <p class="text-gray-400 mt-1">Headcount: {{ maintenance_cost.numberOfDevelopers.toFixed(1) }}
                                devs, {{ maintenance_cost.numberOfProductManagers.toFixed(1) }} PMs, {{
                                    maintenance_cost.numberOfSupport }} support, {{ maintenance_cost.numberOfDevOps }}
                                dev-ops</p>
                            <p v-if="formData.showRange" class="text-gray-500 mt-1">Ranges: devs {{
                                maintenance_math.p10Devs.toFixed(1) }}–{{ maintenance_math.p90Devs.toFixed(1) }}, PMs {{
                                    maintenance_math.p10PMs.toFixed(1) }}–{{ maintenance_math.p90PMs.toFixed(1) }},
                                support {{ maintenance_math.p10Support.toFixed(1) }}–{{
                                    maintenance_math.p90Support.toFixed(1) }}</p>
                        </div>

                        <div class="border border-gray-700/50 rounded-2xl p-4">
                            <h3 class="text-lg font-bold text-white mb-2">Unified vs build</h3>
                            <p class="text-blue-400">Build, first year: <b>{{ money(compare.firstYearTotal) }}</b></p>
                            <p class="text-blue-400">Unified, annual: <b>{{ money(compare.unifiedAnnual) }}</b></p>
                            <p class="text-blue-400">Difference: <b>{{ money(compare.firstYearTotal - compare.unifiedAnnual) }}</b></p>
                            <p class="text-blue-400">Payback: <b v-if="compare.paybackMonths !== null">{{
                                compare.paybackMonths.toFixed(1) }} months</b><span v-else>N/A</span></p>
                        </div>

                        <p class="text-xs text-gray-500">Estimates from industry rules of thumb, not a quote. Adjust
                            the assumptions under Advanced to match your team.</p>
                    </aside>
                </div>
            </div>
        </div>
    </section>
</template>

<script lang="ts">
// Ported from the integrations-build-calculator draft (unified-to/unified-www4#219, components/pricing/Cost.vue).
// Defaults and formulas are unchanged; the estimate functions live in services/integrations-calculator.
import { BUILD_CATEGORIES, computeWeightedComplexity, type BuildIntegrationCategory } from '~/services/integrations-calculator/common';
import { MAP_CATS } from '~/services/categories';
import { formatMoney } from '~/services/pricing';
import { estimateBuildTimeAndCost } from '~/services/integrations-calculator/build';
import { estimateMaintenanceTimeAndCost } from '~/services/integrations-calculator/maintain';

const INPUT =
    'bg-transparent border-b border-gray-700 focus:border-blue-500 text-white text-lg font-medium px-2 py-1 focus:outline-none';

export default {
    data() {
        return {
            INPUT,
            categoryOptions: BUILD_CATEGORIES.map((id) => ({ id, label: (MAP_CATS as Record<string, string>)[id] || id.toUpperCase() })).sort(
                (a, b) => a.label.localeCompare(b.label)
            ),
            formData: {
                categories: ['ats', 'hris'] as BuildIntegrationCategory[],
                numberOfNewIntegrations: 10,
                yearsInBusiness: 5,
                baseBuildWeeks: 12,
                developersAnnualSalary: 120000,
                productManagersAnnualSalary: 120000,
                numberOfExistingIntegrations: 3,
                supportAnnualSalary: 75000,
                devOpsAnnualSalary: 100000,
                numberOfAPICallsPerMonth: 10000,
                ticketsPerIntegrationPerYear: 12,
                hoursPerTicket: 1.5,
                partnerFeesBuild: 0,
                partnerFeesMaintenance: 0,
                unifiedMonthlyCost: 2500,
                showRange: false,
            },
        };
    },
    computed: {
        build_cost() {
            return estimateBuildTimeAndCost(this.formData);
        },
        maintenance_cost() {
            return estimateMaintenanceTimeAndCost(this.formData);
        },
        compare() {
            const buildFirstYear = this.build_cost.costForAllIntegrations;
            const maintAnnual = this.maintenance_cost.annualCost;
            const firstYearTotal = buildFirstYear + maintAnnual;
            const unifiedMonthly = this.formData.unifiedMonthlyCost || 0;
            const unifiedAnnual = unifiedMonthly * 12;
            const monthlyMaint = this.maintenance_cost.monthlyCost || 0;
            // Months of avoided maintenance (net of Unified) needed to cover the build cost. Only meaningful when
            // in-house maintenance costs more per month than Unified; otherwise there is no payback to show.
            const monthlySaving = monthlyMaint - unifiedMonthly;
            const paybackMonths = unifiedAnnual > 0 && monthlySaving > 0 ? buildFirstYear / monthlySaving : null;
            return { buildFirstYear, maintAnnual, firstYearTotal, unifiedAnnual, paybackMonths };
        },
        build_math() {
            const base = this.formData.baseBuildWeeks || 12;
            const complexityFactor = computeWeightedComplexity(this.formData.categories, 'build');
            const y = this.formData.yearsInBusiness;
            const inferredLegacy = y > 4;
            const legacyFactor = inferredLegacy ? (y > 8 ? 1.3 : 1.2) : y > 2 ? 1.1 : 1.0;
            const learningFactor = 1 / (1 + Math.log(1 + (this.formData.numberOfExistingIntegrations || 0)));
            // ±15% band on the complexity × legacy multiplier
            const mult = complexityFactor * legacyFactor;
            const p10Weeks = Math.max(base * mult * 0.85 * learningFactor, base * 0.5);
            const p90Weeks = Math.max(base * mult * 1.15 * learningFactor, base * 0.5);
            return { p10Weeks, p90Weeks };
        },
        maintenance_math() {
            const total = (this.formData.numberOfExistingIntegrations || 0) + (this.formData.numberOfNewIntegrations || 0);
            const complexityFactor = computeWeightedComplexity(this.formData.categories, 'maintain');
            const tickets = this.formData.ticketsPerIntegrationPerYear || 12;
            const hoursPerTicket = this.formData.hoursPerTicket || 1.5;
            const supportAnnualHours = total * tickets * hoursPerTicket;
            // ±15% band on the complexity factor
            return {
                p10Devs: Math.pow(total / 4, 0.8) * (complexityFactor * 0.85),
                p90Devs: Math.pow(total / 4, 0.8) * (complexityFactor * 1.15),
                p10PMs: Math.max(0.5, total / 300) * (complexityFactor * 0.85),
                p90PMs: Math.max(0.5, total / 300) * (complexityFactor * 1.15),
                p10Support: (supportAnnualHours / (40 * 52)) * (0.9 + 0.1 * (complexityFactor * 0.85)),
                p90Support: (supportAnnualHours / (40 * 52)) * (0.9 + 0.1 * (complexityFactor * 1.15)),
            };
        },
    },
    methods: {
        money(amount: number) {
            // formatMoney's default '+' suffix suits plan prices ("$750+"), not computed totals.
            return amount < 0 ? `-${formatMoney(-amount, '$', 0, '')}` : formatMoney(amount, '$', 0, '');
        },
    },
};
</script>
